import { useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import CloseIcon from '@mui/icons-material/Close'
import { Divider } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Fade from '@mui/material/Fade'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Placeholder from '../../../../../images/placeholder120x120.png'
import { DEFAULT_CURRENCY } from '../../../../constants'
import { OutOfStockStatusEnum, ProductPriceRangeType } from '../../../../generated/gqlTypes'
import { CART_URL } from '../../../urls'
import VariantKit from './VariantKit'
import VariantSimple from './VariantSimple'
import { useTranslation } from 'react-i18next'
import FormattedPrice from '@components/FormattedPrice'

// Closes the panel after `duration` ms; the timer is paused while the user hovers or touches the panel
// and restarts from scratch when `resetKey` changes (e.g. another variant was added).
const useAutoClose = (active: boolean, onClose: () => void, duration: number, resetKey?: unknown) => {
  const [paused, setPaused] = useState(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!active) setPaused(false)
  }, [active])

  useEffect(() => {
    if (!active || paused) return
    const timeoutId = setTimeout(() => onCloseRef.current(), duration)
    return () => clearTimeout(timeoutId)
  }, [active, paused, duration, resetKey])

  return {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onTouchStart: () => setPaused(true),
  }
}

export const AddedVariantMsg: React.FC<{
  variant: {
    id: string
    pk: number
    isSupply: boolean
    shippingRequired: boolean
    sku: string
    priceRange: ProductPriceRangeType
    stockQuantity: number
    outOfStockStatus: string
    stockStatus: OutOfStockStatusEnum | null
    properties: {
      id: number
      attributeName: string
      valueName: string
    }[]
  }
  quantity: number
  supplyLinesJson: any
  reflectionName: string
  imgUrl: string | null
  onClosePanel(): void
  hideDuration?: number
}> = ({ variant, imgUrl, reflectionName, onClosePanel, quantity, supplyLinesJson, hideDuration = 5000 }) => {
  const { t } = useTranslation()
  const autoCloseHandlers = useAutoClose(true, onClosePanel, hideDuration, variant)
  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })
  // const lines = cartQueryResult.data?.cart.allLines || []
  // const itemSubtotal = useMemo(() => lines?.map(calculateLineTotal).reduce((prev, curr) => prev + curr, 0), [lines])
  const cartTotalPrice = cartQueryResult.data?.cart?.totalPrice

  return (
    <div className='bg-white' {...autoCloseHandlers}>
      <div className='text-capitalize font-bold text-lg'>
        {t('theProductAlreadyInTheBag', 'The product already in the bag!')}
      </div>
      {variant && (
        <div className='flex py-12'>
          <div className='mr-10 w-90 md:w-120 max-w-full flex-shrink-0'>
            <img
              alt={reflectionName}
              src={imgUrl || Placeholder}
              className='max-w-full'
              style={{ width: 'auto', height: 'auto' }}
            />
          </div>
          <div className='flex-1'>
            <div className='text-sm leading-16 line-clamp-2 mb-5'>{reflectionName}</div>
            {!variant.isSupply && (
              <VariantSimple sku={variant.sku} properties={variant.properties} quantity={quantity} variant={variant} />
            )}
            {supplyLinesJson && (
              <VariantKit
                supplyLines={supplyLinesJson}
                onClosePanel={onClosePanel}
                showKitContent={false}
                quantity={quantity}
                sku={variant.sku}
                properties={variant.properties}
              />
            )}
          </div>
        </div>
      )}
      <Divider />
      <div className='pt-24 text-center font-bold text-base'>
        <span>{t('subtotal', 'Subtotal')}: </span>
        <span>
          {!!cartTotalPrice ? (
            <FormattedPrice
              currency={cartTotalPrice?.currency || DEFAULT_CURRENCY}
              amount={+cartTotalPrice?.discount.gross}
            />
          ) : (
            '...'
          )}
        </span>
      </div>
      <div className='py-12 flex items-center -mx-5'>
        <span className='mx-5 flex-1'>
          <Button variant='outlined' size='large' color='secondary' onClick={onClosePanel} disableElevation fullWidth>
            <span className='whitespace-nowrap font-bold'>{t('button.keepShopping', 'Keep shopping')}</span>
          </Button>
        </span>
        <span className='mx-5 flex-1'>
          <Button
            component={Link}
            to={CART_URL}
            variant='contained'
            size='large'
            color='secondary'
            disableElevation
            fullWidth
          >
            <span className='font-bold'>{t('button.checkout', 'Checkout')}</span>
          </Button>
        </span>
      </div>
    </div>
  )
}

interface IProps {
  isOpen: boolean
  variant: {
    id: string
    pk: string
    isSupply: boolean
    shippingRequired: boolean
    sku: string
    brand: string
    addedQuantity: number
    priceRange: ProductPriceRangeType
    stockQuantity: number
    outOfStockStatus: string
    stockStatus: OutOfStockStatusEnum | null
    properties: {
      id: number
      attributeName: string
      valueName: string
    }[]
    supplyLinesJson: any
  }
  reflectionName: string
  imgNode: any

  onOpen(): void

  onClosePanel(): void

  hideDuration?: number
}

const AddVariantSuccessPanel: React.FC<IProps> = ({
  isOpen,
  onOpen,
  onClosePanel,
  variant,
  reflectionName,
  imgNode,
  hideDuration = 8000,
}) => {
  const { t } = useTranslation()
  const autoCloseHandlers = useAutoClose(isOpen, onClosePanel, hideDuration, variant)

  return (
    <div>
      <SwipeableDrawer
        className='added-variant___panel'
        anchor='right'
        open={isOpen}
        onOpen={onOpen}
        onClose={onClosePanel}
        disableSwipeToOpen
      >
        <div className='flex flex-col h-full' {...autoCloseHandlers}>
          <DialogTitle>
            <span className='text-capitalize font-semibold text-left block'>
              {t('theProductAlreadyInTheBag', 'The product already in the bag!')}
            </span>
            <span className='absolute right-10 top-10'>
              <IconButton onClick={onClosePanel} size='medium' title={t('close', 'Close')}>
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </span>
          </DialogTitle>
          <DialogContent className='w-400 max-w-full' dividers>
            <Fade in={true} timeout={500}>
              <div>
                {variant && (
                  <>
                    <Grid container spacing={1}>
                      <Grid item xs={5}>
                        <Avatar
                          alt={reflectionName}
                          src={imgNode?.url || imgNode?.image || Placeholder}
                          style={{ width: 'auto', height: 'auto' }}
                          variant='square'
                        />
                      </Grid>
                      <Grid item xs={7}>
                        <div className='font-semibold text-13'>{reflectionName}</div>
                        {!variant.isSupply && (
                          <VariantSimple
                            sku={variant.sku}
                            properties={variant.properties}
                            quantity={variant.addedQuantity}
                            variant={variant}
                          />
                        )}
                        {variant.isSupply && variant.supplyLinesJson && (
                          <VariantKit
                            supplyLines={variant.supplyLinesJson}
                            onClosePanel={onClosePanel}
                            quantity={variant.addedQuantity}
                            sku={variant.sku}
                            properties={variant.properties}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </>
                )}
              </div>
            </Fade>
          </DialogContent>
          <DialogActions style={{ padding: '8px 24px' }}>
            <Button
              className='flex-1'
              variant='outlined'
              size='large'
              color='secondary'
              onClick={onClosePanel}
              disableElevation
            >
              <span className='whitespace-nowrap font-bold'>{t('button.keepShopping', 'Keep shopping')}</span>
            </Button>
            <Button
              className='flex-1'
              component={Link}
              to={CART_URL}
              variant='contained'
              size='large'
              color='secondary'
              disableElevation
            >
              <span className='font-bold'>{t('button.checkout', 'Checkout')}</span>
            </Button>
          </DialogActions>
        </div>
      </SwipeableDrawer>
    </div>
  )
}

export default AddVariantSuccessPanel
